/** Che user/pass của proxy: `host:port:****:****`. Chuỗi 2 phần thì để nguyên. */
export function maskProxy(value: string): string {
  const parts = value.split(':');
  if (parts.length < 3) return value;
  return `${parts[0]}:${parts[1]}:****:****`;
}

/** Khoảng thời gian dạng "5 phút trước". Quá 1 ngày thì trả về ngày. */
export function formatRelativeTime(date: Date | string | null | undefined): string {
  if (!date) return '';

  const then = new Date(date).getTime();
  const minutes = Math.floor((Date.now() - then) / 60_000);

  if (minutes < 1) return 'Vài giây trước';
  if (minutes < 60) return `${String(minutes)} phút trước`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${String(hours)} giờ trước`;

  return new Date(then).toLocaleDateString('vi-VN');
}

/** Ngôn ngữ định dạng số/ngày — một chỗ duy nhất, khỏi rải `'vi-VN'` khắp component. */
const LOCALE = 'vi-VN';

/** Số tiền VND có dấu phân cách. `null`/`undefined` coi như 0. */
export function formatMoney(value: number | undefined | null): string {
  return (value ?? 0).toLocaleString(LOCALE);
}

/**
 * Số tiền có thể CHƯA BIẾT.
 *
 * `'?'` khác hẳn `0`: chưa đồng bộ xong mà hiện `0` là báo cho người dùng một
 * con số sai chứ không phải "chưa có dữ liệu".
 */
export function formatMoneyOrUnknown(value: number | '?' | undefined | null): string {
  return value === '?' || value == null ? '?' : value.toLocaleString(LOCALE);
}

/** Như `formatMoneyOrUnknown` nhưng số dương có dấu `+` — dùng cho lãi/lỗ. */
export function formatSignedMoney(value: number | '?' | undefined | null): string {
  if (value === '?' || value == null) return '?';
  return `${value > 0 ? '+' : ''}${value.toLocaleString(LOCALE)}`;
}

/** Ngày dạng `dd/mm/yyyy`. Không có giá trị thì trả `-`. */
export function formatDate(value: Date | string | null | undefined): string {
  return value ? new Date(value).toLocaleDateString(LOCALE) : '-';
}

/** Ngày kèm giờ. Nhận `Date`, chuỗi ISO, hoặc mốc epoch tính bằng GIÂY. */
export function formatDateTime(value: Date | string | number | null | undefined): string {
  if (value == null || value === '') return '-';
  // Nhà cái trả mốc thời gian bằng GIÂY, còn `Date` nhận mili-giây.
  const date = typeof value === 'number' ? new Date(value * 1000) : new Date(value);
  return date.toLocaleString(LOCALE);
}

/**
 * Nội dung một dòng lịch sử cược cho dễ đọc.
 *
 * Xóc Đĩa Live trả sẵn câu sạch ("Kết quả ... Đặt ...: n. Nhận m."), nhưng Xóc
 * Đĩa THƯỜNG trả nguyên cục JSON `{pBS, r, sm, tR}` — nhìn rất khó chịu. May là
 * JSON đó có sẵn `sm` chính là câu sạch tương đương, nên chỉ cần bóc `sm` ra.
 *
 * Không phải JSON (Live, câu trần) → trả NGUYÊN VĂN. JSON thiếu `sm` (shape lạ)
 * → thử dựng lại câu từ kết quả `r` + cửa đặt `pBS` (v>0) + tiền nhận `tR`; dựng
 * không nổi thì vẫn trả raw. Nguyên tắc: thà hiện raw còn hơn nuốt mất nội dung.
 */
export function formatBetDescription(description: string): string {
  const text = description.trim();
  if (!text.startsWith('{')) return description;

  try {
    const parsed = JSON.parse(text) as {
      sm?: unknown;
      r?: unknown;
      pBS?: unknown;
      tR?: unknown;
    };

    if (typeof parsed.sm === 'string' && parsed.sm.trim()) return parsed.sm.trim();

    // Thiếu `sm` — dựng lại từ các mảnh; mảnh nào không đọc được thì bỏ qua.
    const result = Array.isArray(parsed.r)
      ? parsed.r.filter((x): x is string => typeof x === 'string')
      : [];
    const bets = Array.isArray(parsed.pBS)
      ? parsed.pBS
          .filter(
            (b): b is { bE: string; v: number } =>
              typeof b === 'object' &&
              b !== null &&
              typeof (b as { bE?: unknown }).bE === 'string' &&
              typeof (b as { v?: unknown }).v === 'number' &&
              (b as { v: number }).v > 0,
          )
          .map((b) => `${b.bE}: ${formatMoney(b.v)}`)
      : [];

    if (result.length === 0 && bets.length === 0) return description;

    const parts: string[] = [];
    if (result.length > 0) parts.push(`Kết quả: ${result.join(', ')}.`);
    if (bets.length > 0) parts.push(`Đặt ${bets.join(', ')}.`);
    if (typeof parsed.tR === 'number') parts.push(`Nhận ${formatMoney(parsed.tR)}.`);
    return parts.join(' ');
  } catch {
    return description;
  }
}
